import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '../lib/utils';
import { ShowMoreButton } from '../ShowMoreButton';
import { Typography } from '../Typography/Typography';

import type { Props } from './types';

const clampStyle = (maxLines: number): CSSProperties => ({
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: maxLines,
  display: '-webkit-box',
  overflow: 'hidden'
});

/**
 * Text clamped to a few lines, with a toggle that appears only when there is
 * more to read. The clamp depends on the rendered width, so it remeasures when
 * that changes.
 *
 * Shared so each Show more caller composes the same clamping rather than
 * repeating its own measurement and toggle.
 */
function ShowMoreText({ children, className, label, maxLines = 2 }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  // Different text starts collapsed, so a short passage cannot arrive already
  // expanded with a Show less toggle left over from the last.
  useEffect(() => {
    setIsExpanded(false);
  }, [children]);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) return;

    // Only the clamped element reports a useful overflow -- expanded it grows to
    // fit -- so keep the last collapsed measurement while it is open.
    const measure = () => {
      if (isExpanded) return;

      setIsOverflowing(element.scrollHeight > element.clientHeight);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [children, isExpanded, maxLines]);

  return (
    <>
      <div ref={textRef} style={isExpanded ? undefined : clampStyle(maxLines)}>
        <Typography as="span" className={cn('break-words', className)}>
          {children}
        </Typography>
      </div>

      {(isOverflowing || isExpanded) && (
        <ShowMoreButton
          isExpanded={isExpanded}
          label={label}
          onToggle={() => setIsExpanded((expanded) => !expanded)}
        />
      )}
    </>
  );
}

export { ShowMoreText };
